import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {useParams, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import AdminOrdersService from '../services/adminOrdersService';
import CancelOrderDialog, {summarizeCancelResult} from '../components/CancelOrderDialog';
import SectionHeader from '@/shared/components/SectionHeader';
import Badge from '@/shared/components/Badge';
import {dollars} from '@/shared/utils/money';

function actionError(e: unknown): string {
    return (e as {data?: {error?: string}})?.data?.error ?? (e as Error)?.message ?? 'Action failed.';
}

function PushStatusBadge({status}: {status: string | null}) {
    if (status === null) return <Badge variant="neutral">—</Badge>;
    if (status === 'pushed') return <Badge variant="success">{status}</Badge>;
    if (status === 'failed') return <Badge variant="error">{status}</Badge>;
    return <Badge variant="info">{status}</Badge>;
}

const Card: React.FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
    <div className="bg-white rounded-2xl border border-mld-panel-border shadow-sm p-6 space-y-2">
        <h3 className="text-sm font-semibold text-mld-text-primary">{title}</h3>
        {children}
    </div>
);

// Embedded in the admin shell: parent passes `orderId` + `onBack`. Standalone
// (deep-link) usage falls back to the route param + router navigation.
const AdminOrderDetailView: React.FC<{orderId?: string; onBack?: () => void}> = ({orderId, onBack}) => {
    const {id: routeId = ''} = useParams();
    const id = orderId ?? routeId;
    const navigate = useNavigate();
    const goBack = () => (onBack ? onBack() : navigate('/admin/orders'));
    const {
        data: o,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['admin', 'order', id],
        queryFn: () => AdminOrdersService.detail(id),
        enabled: id.length > 0,
    });

    const qc = useQueryClient();
    const invalidate = () => qc.invalidateQueries({queryKey: ['admin', 'order', id]});
    const [cancelOpen, setCancelOpen] = useState(false);
    const cancelMut = useMutation({
        mutationFn: (payload: {scope: 'order' | 'purchase'; refund: boolean; reason?: string}) =>
            AdminOrdersService.cancelOrder(id, payload),
        onSuccess: (result) => {
            toast.success(summarizeCancelResult(result));
            setCancelOpen(false);
            invalidate();
            qc.invalidateQueries({queryKey: ['admin', 'orders']});
        },
        onError: (e) => toast.error(actionError(e)),
    });
    const refundMut = useMutation({
        mutationFn: (amount_cents: number | undefined) => AdminOrdersService.refundOrder(id, {amount_cents}),
        onSuccess: (r) => {
            toast.success(
                r.fullyRefunded ? 'Order fully refunded.' : `Refunded ${(r.refundedCents / 100).toFixed(2)}.`
            );
            invalidate();
        },
        onError: (e) => toast.error(actionError(e)),
    });
    const shipMut = useMutation({
        mutationFn: () => AdminOrdersService.sendToShipStation(id),
        onSuccess: () => {
            toast.success('Push queued — status will update shortly.');
            invalidate();
        },
        onError: (e) => toast.error(actionError(e)),
    });
    const onRefund = () => {
        const raw = window.prompt('Refund amount in dollars (leave blank for a full refund):', '');
        if (raw === null) return;
        const trimmed = raw.trim();
        const amount_cents = trimmed === '' ? undefined : Math.round(parseFloat(trimmed) * 100);
        if (amount_cents !== undefined && (!Number.isFinite(amount_cents) || amount_cents <= 0)) {
            toast.error('Enter a positive dollar amount, or leave blank for full refund.');
            return;
        }
        refundMut.mutate(amount_cents);
    };

    if (isLoading) return <div className="p-6 text-gray-500">Loading…</div>;
    if (isError || !o) return <div className="p-6 text-mld-status-high">Order not found.</div>;

    return (
        <div className="p-6 space-y-4">
            <button className="text-sm text-mld-primary" onClick={goBack}>
                ← All orders
            </button>
            <SectionHeader title={`Order ${o.order_number}`} />

            <Card title="Actions">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setCancelOpen(true)}
                        disabled={cancelMut.isPending}
                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-mld-status-high disabled:opacity-40">
                        Cancel order
                    </button>
                    <button
                        type="button"
                        onClick={onRefund}
                        disabled={refundMut.isPending}
                        className="rounded-md border border-gray-300 px-3 py-1 text-sm text-mld-text-primary disabled:opacity-40">
                        Refund…
                    </button>
                </div>
            </Card>

            <CancelOrderDialog
                open={cancelOpen}
                order={o}
                pending={cancelMut.isPending}
                onClose={() => setCancelOpen(false)}
                onConfirm={(payload) => cancelMut.mutate(payload)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card title="Customer">
                    <p className="text-mld-text-primary">{o.customer_name}</p>
                    <p className="text-gray-500 text-sm">{o.customer_email}</p>
                    <p className="text-gray-500 text-sm">{o.customer_phone ?? ''}</p>
                    <p className="text-gray-500 text-xs">Org: {o.organization_name ?? o.organization_id}</p>
                </Card>
                <Card title="Money">
                    <p className="text-mld-text-primary">Total {dollars(o.total_cents)}</p>
                    <p className="text-gray-500 text-sm">
                        Patient {dollars(o.patient_total_cents)} · Employer {dollars(o.employer_total_cents)}
                    </p>
                    <p className="text-gray-500 text-sm">Discount {dollars(o.discount_cents)}</p>
                </Card>
                <Card title="Payment">
                    {o.payment ? (
                        <>
                            <p className="text-mld-text-primary">
                                {o.payment.provider_key} · {o.payment.status}
                            </p>
                            <p className="text-gray-500 text-sm">{dollars(o.payment.amount_cents)}</p>
                            <p className="text-gray-500 text-xs">{o.payment.provider_payment_id ?? ''}</p>
                        </>
                    ) : (
                        <p className="text-gray-500 text-sm">No payment recorded.</p>
                    )}
                </Card>
                <Card title="Fulfillment">
                    <p className="text-mld-text-primary">{o.fulfillment.status}</p>
                    <p className="text-gray-500 text-sm">
                        {o.fulfillment.tracking_carrier ?? ''} {o.fulfillment.tracking_number ?? ''}
                    </p>
                    <p className="text-gray-500 text-xs">Shipped: {o.fulfillment.shipped_at?.slice(0, 10) ?? '—'}</p>

                    <div className="mt-2 space-y-1 border-t border-mld-panel-border pt-2">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">Push status</span>
                            <PushStatusBadge status={o.fulfillment.push_status} />
                        </div>
                        <p className="text-gray-500 text-xs">
                            ShipStation ID: {o.fulfillment.shipstation_order_id ?? '—'}
                        </p>
                        <p className="text-gray-500 text-xs">Attempts: {o.fulfillment.push_attempts}</p>
                        {o.fulfillment.push_status === 'failed' && (
                            <p className="text-red-600 text-sm">{o.fulfillment.push_error}</p>
                        )}
                        <button
                            type="button"
                            onClick={() => shipMut.mutate()}
                            disabled={shipMut.isPending || o.fulfillment.push_status === null}
                            className="rounded-md border border-gray-300 px-3 py-1 text-sm text-mld-text-primary disabled:opacity-40">
                            Retry push
                        </button>
                    </div>
                </Card>
            </div>

            <Card title="Line items">
                <table className="min-w-full text-sm">
                    <tbody>
                        {o.line_items.map((li) => (
                            <tr
                                key={`${li.kind}-${li.name}`}
                                className="border-b border-mld-panel-border last:border-0">
                                <td className="py-2 text-mld-text-primary">{li.name}</td>
                                <td className="py-2 text-gray-500">×{li.quantity}</td>
                                <td className="py-2 text-right">{dollars(li.total_cents)}</td>
                            </tr>
                        ))}
                        {o.line_items.length === 0 && (
                            <tr>
                                <td className="py-2 text-gray-500">No line items.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Card>

            {o.subscription && (
                <Card title="Subscription">
                    <p className="text-mld-text-primary">
                        {o.subscription.enrollment_kind} · {o.subscription.status} · next due{' '}
                        {o.subscription.next_test_due ?? '—'}
                    </p>
                    <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Cycle history</p>
                        <table className="min-w-full text-sm">
                            <tbody>
                                {o.subscription.cycle_history.map((c) => (
                                    <tr key={c.order_id} className="border-b border-mld-panel-border last:border-0">
                                        <td className="py-1.5 text-mld-text-primary">{c.order_number}</td>
                                        <td className="py-1.5 text-gray-500">{c.created_at.slice(0, 10)}</td>
                                        <td className="py-1.5 text-gray-500">{c.fulfillment_status}</td>
                                        <td className="py-1.5 text-right">{dollars(c.total_cents)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default AdminOrderDetailView;
